
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Trash2, FileText, Languages, Clock } from "lucide-react";
import { StudyNote } from "@/types/notes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";

interface StudyNoteCardProps {
  note: StudyNote;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (id: string) => void;
  isAdmin: boolean;
}

export const StudyNoteCard: React.FC<StudyNoteCardProps> = ({
  note,
  isSelected,
  onSelect,
  onDelete,
  isAdmin,
}) => {
  const { isMobile } = useIsMobile();
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const getLanguageLabel = (langCode?: string) => {
    switch (langCode) {
      case 'hi':
        return 'Hindi';
      case 'bn':
        return 'Bengali';
      case 'te':
        return 'Telugu';
      case 'ta':
        return 'Tamil';
      case 'mr':
        return 'Marathi';
      case 'gu':
        return 'Gujarati';
      case 'kn':
        return 'Kannada';
      case 'ml':
        return 'Malayalam';
      case 'pa':
        return 'Punjabi';
      case 'or':
        return 'Odia';
      case 'en':
        return 'English';
      case 'all':
        return 'All Languages';
      default:
        return 'English';
    }
  };

  const getLanguageColor = (langCode?: string) => {
    switch (langCode) {
      case 'hi':
        return 'bg-secondary/20 text-secondary';
      case 'bn':
        return 'bg-purple-100 text-purple-700';
      case 'te':
        return 'bg-indigo-100 text-indigo-700';
      case 'ta':
        return 'bg-pink-100 text-pink-700';
      case 'mr':
        return 'bg-orange-100 text-orange-700';
      case 'gu':
        return 'bg-green-100 text-green-700';
      case 'kn':
        return 'bg-cyan-100 text-cyan-700';
      case 'ml':
        return 'bg-teal-100 text-teal-700';
      case 'pa':
        return 'bg-amber-100 text-amber-700';
      case 'or':
        return 'bg-lime-100 text-lime-700';
      case 'all':
        return 'bg-blue-100 text-blue-700';
      case 'en':
        return 'bg-primary/20 text-primary';
      default:
        return 'bg-primary/20 text-primary';
    }
  };

  const handleNoteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (typeof onSelect === 'function') {
      onSelect();
    }
  };

  // Enhanced touch handling specifically for mobile
  const handleTouchStart = () => {
    const element = document.getElementById(`note-card-${note.id}`);
    if (element) {
      element.classList.add('bg-secondary/10');
    }
  };

  const handleTouchEnd = () => {
    const element = document.getElementById(`note-card-${note.id}`);
    if (element) {
      element.classList.remove('bg-secondary/10');
      // Ensure some delay to prevent accidental touches
      setTimeout(() => {
        if (typeof onSelect === 'function') {
          onSelect();
        }
      }, 50);
    }
  };

  return (
    <Card
      id={`note-card-${note.id}`}
      className={`cursor-pointer transition-all duration-200 ${
        isSelected ? "bg-secondary/10 border-secondary shadow-sm" : "hover:bg-secondary/5"
      }`}
      onClick={handleNoteClick}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <CardContent className="p-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-medium line-clamp-1 text-gray-800 text-xs">{note.title}</h3>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              <span className="inline-flex items-center px-1 py-0.5 text-[10px] font-medium rounded-full bg-secondary/10 text-secondary">
                {note.subject}
              </span>
              <span className={`inline-flex items-center px-1 py-0.5 text-[10px] font-medium rounded-full ${getLanguageColor(note.language)}`}>
                <Languages className="h-2 w-2 mr-0.5" />
                {getLanguageLabel(note.language)}
              </span>
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground flex items-center">
              <Clock className="h-2 w-2 mr-0.5" /> {formatDate(note.createdAt)}
            </div>
          </div>
          <div className="ml-2 p-1 rounded-full bg-gray-50">
            <FileText className="h-3 w-3 text-secondary" />
          </div>
        </div>
      </CardContent>
      {isAdmin && (
        <CardFooter className="p-0 border-t">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full rounded-none text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors text-xs py-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this study note? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="transition-all duration-200 hover:bg-gray-100">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-gradient-to-r from-destructive to-red-600 text-destructive-foreground hover:from-destructive/90 hover:to-red-700 transition-all duration-300 hover:shadow-md active:scale-95"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(note.id);
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardFooter>
      )}
    </Card>
  );
};
